import type { components } from '@/api'
import type { APIResult } from '@/backend/types'
import type {
  ResourceClientData,
  ResourceMemberRegistryItem,
} from '@/modules/IdentityAndAccess/resourceMemberRegistry/types'
import { serializeResourceMember } from '@/modules/IdentityAndAccess/serializers'
import type {
  AssignableRole,
  ResourceAccessType,
  ResourceMember,
  ResourceType,
} from '@/modules/IdentityAndAccess/types'
import { getResourceMemberDisplayName } from '@/modules/IdentityAndAccess/utils'
import { invariant } from '@/shared/utils/typeAssertions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue } from 'vue'

/**
 * Builds a composable function that can be used to manage the members of a resource.
 * The composable function will provide global persisted state for the members of the
 * resource, and will provide methods to add, update, and delete members.
 */
export const buildResourceClient = ({
  loadMembers,
  addResourceMember,
  updateResourceMember,
  removeResourceMember,
  updateAccessType,
}: {
  /** Backend API to list all members of the resource. */
  loadMembers: LoadMembers
  /** Backend API to add an explicitly assigned member to the resource. */
  addResourceMember: AddResourceMember
  /** Backend API to update the role of an explicitly assigned member. */
  updateResourceMember: UpdateResourceMember
  /** Backend API to remove an explicitly assigned member from the resource. */
  removeResourceMember: RemoveResourceMember
  /** Backend API to update the default access type of the resource. */
  updateAccessType: UpdateAccessType
}): ResourceMemberRegistryItem<ResourceType>['useResourceMembers'] => {
  return ({
    resourceId,
    workspaceId,
    disableLoadOnMount = false,
  }): ReturnType<ResourceMemberRegistryItem<ResourceType>['useResourceMembers']> => {
    const queryKey = computed(() => ['resource-members', toValue(resourceId)])

    const queryClient = useQueryClient()

    /**
     * Query to load all members of the resource and the default access type.
     */
    const loadMembersQuery = useQuery({
      queryKey,
      queryFn: async () => {
        const res = await loadMembers({
          resourceId: toValue(resourceId),
          workspaceId: toValue(workspaceId),
        })

        if (!res.ok) {
          throw new Error(res.error.message)
        }

        return {
          members: res.data.data
            .filter((m) => typeof m.user !== 'string')
            .map(serializeResourceMember),
          accessType: res.data.access_type,
        }
      },
      enabled: () => !disableLoadOnMount && !!toValue(resourceId),
      staleTime: 60_000, // 1 minute
      gcTime: 60_000, // 1 minutes
    })

    /** Mutation to add a member to the resource. */
    const addMemberMutation = useMutation({
      /** Make the API call to add a member to the resource */
      mutationFn: async (params: { workspaceMember: ResourceMember; role: AssignableRole }) => {
        const res = await addResourceMember({
          resourceId: toValue(resourceId),
          workspaceId: toValue(workspaceId),
          userId: params.workspaceMember.id,
          role: params.role,
        })

        if (!res.ok) {
          throw new Error(res.error.message)
        }

        return res
      },

      /** Optimistically add a member to the resource */
      onMutate: ({ role, workspaceMember }) => {
        const optimisticMember: ResourceMember = {
          ...workspaceMember,
          role,
          missingAccess: [],
        }
        queryClient.setQueryData<ResourceClientData>(queryKey, (old) => {
          if (!old) {
            return old
          }

          return {
            ...old,
            members: [...(old?.members ?? []), optimisticMember],
          }
        })

        return { optimisticMember }
      },

      /** Revert the optimistic addition if the mutation fails */
      onError: (error, variables, context) => {
        if (context) {
          queryClient.setQueryData<ResourceClientData>(queryKey, (old) => {
            if (!old) {
              return old
            }

            return {
              ...old,
              members: old.members.filter((m) => m.id !== variables.workspaceMember.id),
            }
          })
        }
      },
    })

    /** Public method to add a member to the resource. */
    const addMember = async (params: { workspaceMember: ResourceMember; role: AssignableRole }) => {
      const res = await addMemberMutation.mutateAsync(params)
      return res
    }

    /** Mutation to update a member's role. */
    const updateMemberMutation = useMutation({
      mutationFn: async (params: { userId: string; role: AssignableRole }) => {
        const res = await updateResourceMember({
          resourceId: toValue(resourceId),
          workspaceId: toValue(workspaceId),
          userId: params.userId,
          role: params.role,
        })

        if (!res.ok) {
          throw new Error(res.error.message)
        }

        return res
      },

      /** Optimistically update a member's role */
      onMutate: (data) => {
        const existingMember = loadMembersQuery.data.value?.members.find(
          (m) => m.id === data.userId,
        )
        invariant(existingMember, 'Existing member not found')

        const oldRole = existingMember?.role
        existingMember.role = data.role

        const revert = () => {
          existingMember.role = oldRole
        }
        return { revert }
      },

      /** Revert the optimistic update if the mutation fails */
      onError: (error, variables, context) => {
        if (context) {
          context.revert()
        }
      },
    })

    /** Public method to update a member's role. */
    const updateMember = async (params: { userId: string; role: AssignableRole }) => {
      const res = await updateMemberMutation.mutateAsync(params)
      return res
    }

    /** Mutation to delete a member from the resource. */
    const deleteMemberMutation = useMutation({
      mutationFn: async (params: { userId: string }) => {
        const res = await removeResourceMember({
          resourceId: toValue(resourceId),
          workspaceId: toValue(workspaceId),
          userId: params.userId,
        })

        if (!res.ok) {
          throw new Error(res.error.message)
        }

        return res
      },

      onMutate: (data) => {
        const oldMemberIndex =
          loadMembersQuery.data.value?.members.findIndex((m) => m.id === data.userId) ?? -1

        const oldMember = loadMembersQuery.data.value?.members[oldMemberIndex]

        queryClient.setQueryData<ResourceClientData>(queryKey, (old) => {
          if (!old) {
            return old
          }

          return {
            ...old,
            members: old.members.filter((m) => m.id !== data.userId),
          }
        })

        return { oldMemberIndex, oldMember }
      },

      onError: (error, variables, context) => {
        if (!context) {
          return
        }

        queryClient.setQueryData<ResourceClientData>(queryKey, (old) => {
          if (old && context.oldMember) {
            old.members.splice(context.oldMemberIndex, 0, context.oldMember)
          }

          return old
        })
      },
    })

    /** Public method to delete a member from the resource. */
    const deleteMember = async (params: { userId: string }) => {
      const res = await deleteMemberMutation.mutateAsync(params)
      return res
    }

    /** Mutation to update the default access type of the resource. */
    const updateDefaultAccessTypeMutation = useMutation({
      mutationFn: async (params: {
        accessType: components['schemas']['Users.UpdateResourceAccessTypeRequest']['access_type']
      }) => {
        const res = await updateAccessType({
          resourceId: toValue(resourceId),
          workspaceId: toValue(workspaceId),
          accessType: params.accessType,
        })

        if (!res.ok) {
          throw new Error(res.error.message)
        }

        return res
      },

      onMutate: (data) => {
        queryClient.setQueryData<ResourceClientData>(queryKey, (old) => {
          if (!old) {
            return old
          }

          return {
            ...old,
            accessType: data.accessType,
          }
        })
      },
    })

    /** Public method to update the default access type of the resource. */
    const updateDefaultAccessType = async (params: {
      accessType: components['schemas']['Users.UpdateResourceAccessTypeRequest']['access_type']
    }) => {
      const res = await updateDefaultAccessTypeMutation.mutateAsync(params)
      return res
    }

    /**
     * Computed properties to get the members and default access type of the resource.
     */
    const members = computed(() => loadMembersQuery.data.value?.members || [])
    const accessType = computed(() => loadMembersQuery.data.value?.accessType || 'private')

    const getMember = (userId: string) => {
      return members.value.find((m) => m.id === userId)
    }

    const getMemberNameFromId = (id: string) => {
      const member = getMember(id)
      if (!member) return id
      return getResourceMemberDisplayName(member)
    }

    return {
      members,
      accessType,
      loadMembers: () => loadMembersQuery.refetch({ cancelRefetch: true }),
      addMember,
      updateMember,
      deleteMember,
      updateDefaultAccessType,
      query: loadMembersQuery,
      getMember,
      getMemberNameFromId,
    }
  }
}

type ResourceMemberResponse = components['schemas']['Users.ResourceMemberResponse']

type LoadMembers = (params: { resourceId: string; workspaceId: string }) => Promise<
  APIResult<{
    access_type: ResourceAccessType
    data: components['schemas']['Users.ResourceMemberResponse'][]
  }>
>

type AddResourceMember = (params: {
  resourceId: string
  workspaceId: string
  userId: string
  role: AssignableRole
}) => Promise<APIResult<ResourceMemberResponse>>

type UpdateResourceMember = (params: {
  resourceId: string
  workspaceId: string
  userId: string
  role: AssignableRole
}) => Promise<APIResult<ResourceMemberResponse>>

type RemoveResourceMember = (params: {
  resourceId: string
  workspaceId: string
  userId: string
}) => Promise<APIResult<ResourceMemberResponse>>

type UpdateAccessType = (params: {
  resourceId: string
  workspaceId: string
  accessType: components['schemas']['Users.UpdateResourceAccessTypeRequest']['access_type']
}) => Promise<APIResult<components['schemas']['Users.ListResourceMembersResponse']>>
